import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Download, Upload, Eye, EyeOff, Sparkles, Key, Copy, Check, ChevronDown, ChevronUp, Loader2, ExternalLink } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import { downloadTextFile } from '@/lib/downloadFile';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { QRCodeCanvas } from '@/components/ui/qrcode';
import { toast } from '@/hooks/useToast';
import {
  useLoginActions,
  generateNostrConnectParams,
  generateNostrConnectURI,
  type NostrConnectParams,
} from '@/hooks/useLoginActions';
import { useNostrPublish } from '@/hooks/useNostrPublish';
import { useUploadFile } from '@/hooks/useUploadFile';
import { useIsMobile } from '@/hooks/useIsMobile';
import { generateSecretKey, getPublicKey, nip19 } from 'nostr-tools';

interface AuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
}

type Step = 'welcome' | 'generate' | 'secure' | 'profile' | 'login' | 'connect';

const validateNsec = (nsec: string) => {
  return /^nsec1[a-zA-Z0-9]{58}$/.test(nsec);
};

const AuthDialog: React.FC<AuthDialogProps> = ({ isOpen, onClose }) => {
  const [step, setStep] = useState<Step>('welcome');
  const [nsec, setNsec] = useState('');
  const [loginNsec, setLoginNsec] = useState('');
  const [showKey, setShowKey] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [showMoreOptions, setShowMoreOptions] = useState(false);
  const [profileData, setProfileData] = useState({
    name: '',
    about: '',
    picture: ''
  });
  
  // Nostrconnect state
  const [nostrConnectParams, setNostrConnectParams] = useState<NostrConnectParams | null>(null);
  const [nostrConnectUri, setNostrConnectUri] = useState<string>('');
  const [isWaitingForConnect, setIsWaitingForConnect] = useState(false);
  const [connectError, setConnectError] = useState<string | null>(null);
  const [uriCopied, setUriCopied] = useState(false);
  const [showBunkerInput, setShowBunkerInput] = useState(false);
  const [bunkerUri, setBunkerUri] = useState('');
  
  const login = useLoginActions();
  // Stable ref so the nostrconnect effect doesn't restart on every render
  const loginRef = useRef(login);
  loginRef.current = login;
  const { mutateAsync: publishEvent, isPending: isPublishing } = useNostrPublish();
  const { mutateAsync: uploadFile, isPending: isUploading } = useUploadFile();
  const avatarFileInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const isMobile = useIsMobile();

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setStep('welcome');
      setNsec('');
      setLoginNsec('');
      setShowKey(false);
      setIsGenerating(false);
      setIsLoggingIn(false);
      setLoginError('');
      setShowMoreOptions(false);
      setProfileData({ name: '', about: '', picture: '' });
      // Reset nostrconnect state
      setNostrConnectParams(null);
      setNostrConnectUri('');
      setIsWaitingForConnect(false);
      setConnectError(null);
      setUriCopied(false);
      setShowBunkerInput(false);
      setBunkerUri('');
      // Abort any pending connections
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
        abortControllerRef.current = null;
      }
    }
  }, [isOpen]);

  // Check if running on actual mobile device (not just small screen)
  const isMobileDevice = typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  // Generate nostrconnect params
  const generateConnectSession = useCallback(() => {
    const relayUrls = login.getRelayUrls();
    const params = generateNostrConnectParams(relayUrls);
    const uri = generateNostrConnectURI(params, {
      name: 'Espy',
      callback: isMobileDevice ? `${window.location.origin}/remoteloginsuccess` : undefined,
    });
    setNostrConnectParams(params);
    setNostrConnectUri(uri);
    setConnectError(null);
  }, [login, isMobileDevice]);

  // Start listening for connection (async) - runs after params are set.
  useEffect(() => {
    if (!nostrConnectParams || isWaitingForConnect) return;

    let cancelled = false;

    const startListening = async () => {
      setIsWaitingForConnect(true);
      abortControllerRef.current = new AbortController();

      try {
        await loginRef.current.nostrconnect(nostrConnectParams, abortControllerRef.current.signal);
        if (!cancelled) {
          onClose();
        }
      } catch (error) {
        if (cancelled) return;
        // AbortError means we intentionally aborted (dialog closed or retry)
        if (error instanceof Error && error.name === 'AbortError') return;
        console.error('Nostrconnect failed:', error);
        setConnectError(error instanceof Error ? error.message : String(error));
        setIsWaitingForConnect(false);
      }
    };

    startListening();

    return () => {
      cancelled = true;
    };
  // NOTE: isWaitingForConnect is intentionally excluded from deps. It is only
  // used as a guard to prevent a second run from spawning a duplicate listener.
  // Including it would cancel the first run the moment it sets the flag to true.
  // login and onClose are accessed via refs/stable callbacks to prevent the
  // effect from restarting mid-subscription and setting cancelled=true.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nostrConnectParams]);

  const handleConnectRetry = useCallback(() => {
    setNostrConnectParams(null);
    setNostrConnectUri('');
    setIsWaitingForConnect(false);
    setConnectError(null);
    setTimeout(() => generateConnectSession(), 0);
  }, [generateConnectSession]);

  const handleCopyUri = async () => {
    await navigator.clipboard.writeText(nostrConnectUri);
    setUriCopied(true);
    setTimeout(() => setUriCopied(false), 2000);
  };

  const handleOpenSignerApp = () => {
    if (!nostrConnectUri) return;
    window.location.href = nostrConnectUri;
  };

  const handleBunkerLogin = async () => {
    if (!bunkerUri.trim() || !bunkerUri.startsWith('bunker://')) return;

    setIsLoggingIn(true);
    try {
      await login.bunker(bunkerUri);
      onClose();
    } catch {
      setConnectError('Failed to connect. Check the URI.');
      setIsLoggingIn(false);
    }
  };

  const goToConnect = () => {
    setStep('connect');
    if (!nostrConnectParams && !connectError) {
      generateConnectSession();
    }
  };

  const generateKey = () => {
    setIsGenerating(true);
    
    // Dramatic pause for the magic effect
    setTimeout(() => {
      const sk = generateSecretKey();
      setNsec(nip19.nsecEncode(sk));
      setStep('secure');
      setIsGenerating(false);
    }, 1500);
  };

  const downloadAndProceed = async () => {
    try {
      const decoded = nip19.decode(nsec);
      if (decoded.type !== 'nsec') {
        throw new Error('Invalid nsec key');
      }

      const pubkey = getPublicKey(decoded.data);
      const npub = nip19.npubEncode(pubkey);
      const filename = `espy-key-${npub.slice(5, 9)}.txt`;

      await downloadTextFile(filename, nsec);

      if (Capacitor.getPlatform() === 'android') {
        toast({ title: 'Key saved', description: `Saved to Download/${filename}` });
      }

      login.nsec(nsec);
      setStep('profile');
    } catch {
      toast({
        title: 'Download failed',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleLogin = () => {
    if (!loginNsec.trim()) {
      setLoginError('Enter your secret key');
      return;
    }

    if (!validateNsec(loginNsec)) {
      setLoginError('Invalid key format');
      return;
    }

    setIsLoggingIn(true);
    setLoginError('');

    setTimeout(() => {
      try {
        login.nsec(loginNsec);
        onClose();
      } catch {
        setLoginError('Could not log in with this key');
        setIsLoggingIn(false);
      }
    }, 50);
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const content = event.target?.result as string;
      if (content && validateNsec(content.trim())) {
        setLoginNsec(content.trim());
      } else {
        setLoginError('File does not contain a valid key');
      }
    };
    reader.readAsText(file);
  };

  const handleExtensionLogin = async () => {
    if (!('nostr' in window)) return;
    
    setIsLoggingIn(true);
    try {
      await login.extension();
      onClose();
    } catch {
      setLoginError('Extension login failed');
      setIsLoggingIn(false);
    }
  };



  const handleAvatarUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      toast({ title: 'Please select an image', variant: 'destructive' });
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: 'Image too large (max 5MB)', variant: 'destructive' });
      return;
    }

    try {
      const tags = await uploadFile(file);
      const url = tags[0]?.[1];
      if (url) {
        setProfileData(prev => ({ ...prev, picture: url }));
      }
    } catch {
      toast({ title: 'Upload failed', variant: 'destructive' });
    }
  };

  const finishSignup = async (skipProfile = false) => {
    try {
      if (!skipProfile && (profileData.name || profileData.about || profileData.picture)) {
        const metadata: Record<string, string> = {};
        if (profileData.name) metadata.name = profileData.name;
        if (profileData.about) metadata.about = profileData.about;
        if (profileData.picture) metadata.picture = profileData.picture;

        await publishEvent({
          kind: 0,
          content: JSON.stringify(metadata),
        });
      }
    } catch {
      // Continue anyway
    } finally {
      onClose();
    }
  };

  const hasExtension = 'nostr' in window;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[95vw] sm:max-w-md p-0 gap-0 overflow-hidden rounded-3xl border-0 bg-gradient-to-br from-rose-50 via-amber-50 to-sky-50 dark:from-neutral-900 dark:via-neutral-900 dark:to-neutral-800">
        <div className="p-6 space-y-6 min-w-0 overflow-hidden">
          
          {/* Welcome Step */}
          {step === 'welcome' && (
            <div className="text-center space-y-6">
              {/* Rainbow eye hero */}
              <div className="relative w-24 h-24 mx-auto">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-rose-300 via-amber-200 via-emerald-200 to-sky-300 animate-pulse" />
                <div className="absolute inset-3 rounded-full bg-gradient-to-tr from-amber-100 via-rose-100 to-violet-100 dark:from-amber-900/50 dark:via-rose-900/50 dark:to-violet-900/50" />
                <div className="absolute inset-0 flex items-center justify-center text-4xl">
                  👁️
                </div>
              </div>

              <div className="space-y-2">
                <h2 className="text-2xl font-semibold">Welcome to Espy</h2>
                <p className="text-muted-foreground">
                  See beauty. Share color.
                </p>
              </div>

              {/* New user - big friendly button */}
              <Button
                onClick={() => setStep('generate')}
                className="w-full py-6 text-lg rounded-full bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400 hover:from-rose-500 hover:via-amber-500 hover:to-emerald-500 text-white shadow-lg"
              >
                <Sparkles className="w-5 h-5 mr-2" />
                Create my account
              </Button>

              {/* Returning user */}
              <button
                onClick={() => setStep('login')}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                I already have an account
              </button>
            </div>
          )}

          {/* Generate Step */}
          {step === 'generate' && (
            <div className="text-center space-y-6">
              <div className="relative w-20 h-20 mx-auto">
                {isGenerating ? (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 rounded-full border-4 border-amber-300 border-t-rose-400 animate-spin" />
                    <Sparkles className="absolute w-6 h-6 text-amber-500 animate-pulse" />
                  </div>
                ) : (
                  <div className="absolute inset-0 rounded-full bg-gradient-to-br from-amber-200 to-rose-200 dark:from-amber-800 dark:to-rose-800 flex items-center justify-center">
                    <Key className="w-8 h-8 text-amber-700 dark:text-amber-300" />
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <h2 className="text-xl font-semibold">
                  {isGenerating ? 'Creating your key...' : 'Your magic key'}
                </h2>
                <p className="text-muted-foreground text-sm">
                  {isGenerating 
                    ? 'Weaving something special just for you'
                    : 'This key is your identity. Keep it safe!'}
                </p>
              </div>

              {!isGenerating && (
                <Button
                  onClick={generateKey}
                  className="w-full py-5 rounded-full bg-gradient-to-r from-amber-400 to-rose-400 hover:from-amber-500 hover:to-rose-500 text-white"
                >
                  <Sparkles className="w-5 h-5 mr-2" />
                  Generate my key
                </Button>
              )}

              <button
                onClick={() => setStep('welcome')}
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                Back
              </button>
            </div>
          )}

          {/* Secure Step */}
          {step === 'secure' && (
            <div className="space-y-5">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-emerald-200 to-sky-200 dark:from-emerald-800 dark:to-sky-800 flex items-center justify-center">
                  <Key className="w-7 h-7 text-emerald-700 dark:text-emerald-300" />
                </div>
                <h2 className="text-xl font-semibold">Save your key</h2>
                <p className="text-muted-foreground text-sm">
                  Store it somewhere safe. You'll need it to log in.
                </p>
              </div>

              {/* Key display */}
              <div className="p-3 rounded-2xl bg-white/50 dark:bg-black/20 border border-amber-200 dark:border-amber-800 overflow-hidden">
                <div className="flex items-center gap-2 min-w-0">
                  <code className="flex-1 min-w-0 text-xs font-mono overflow-hidden whitespace-nowrap">
                    {showKey ? nsec : <span className="tracking-widest">{'•'.repeat(nsec.length)}</span>}
                  </code>
                  <button
                    onClick={() => setShowKey(!showKey)}
                    className="p-1.5 rounded-full hover:bg-black/5 dark:hover:bg-white/10 flex-shrink-0"
                  >
                    {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Warning */}
              <div className="p-3 rounded-xl bg-amber-100/50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700">
                <p className="text-xs text-amber-800 dark:text-amber-200 text-center">
                  ⚠️ This is your only way to access your account. Don't lose it!
                </p>
              </div>

              <Button
                onClick={downloadAndProceed}
                className="w-full py-5 rounded-full bg-gradient-to-r from-emerald-400 to-sky-400 hover:from-emerald-500 hover:to-sky-500 text-white"
              >
                <Download className="w-4 h-4 mr-2" />
                Download &amp; continue
              </Button>
            </div>
          )}

          {/* Profile Step */}
          {step === 'profile' && (
            <div className="space-y-5">
              <div className="text-center space-y-2">
                <h2 className="text-xl font-semibold">Almost there!</h2>
                <p className="text-muted-foreground text-sm">
                  Tell us a bit about yourself (optional)
                </p>
              </div>

              <div className={`space-y-4 ${isPublishing ? 'opacity-50 pointer-events-none' : ''}`}>
                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Name</label>
                  <Input
                    value={profileData.name}
                    onChange={(e) => setProfileData(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="What should we call you?"
                    className="rounded-xl"
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium">About</label>
                  <Textarea
                    value={profileData.about}
                    onChange={(e) => setProfileData(prev => ({ ...prev, about: e.target.value }))}
                    placeholder="A little about you..."
                    className="rounded-xl resize-none"
                    rows={2}
                  />
                </div>

                <div className="space-y-1.5">
                  <label className="text-sm font-medium">Picture</label>
                  <div className="flex gap-2">
                    <Input
                      value={profileData.picture}
                      onChange={(e) => setProfileData(prev => ({ ...prev, picture: e.target.value }))}
                      placeholder="https://..."
                      className="rounded-xl flex-1"
                    />
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      ref={avatarFileInputRef}
                      onChange={handleAvatarUpload}
                    />
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={() => avatarFileInputRef.current?.click()}
                      disabled={isUploading}
                      className="rounded-xl"
                    >
                      {isUploading ? (
                        <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Button
                  onClick={() => finishSignup(false)}
                  disabled={isPublishing}
                  className="w-full py-5 rounded-full bg-gradient-to-r from-rose-400 via-amber-400 to-emerald-400 text-white"
                >
                  {isPublishing ? 'Creating...' : 'Finish'}
                </Button>
                <button
                  onClick={() => finishSignup(true)}
                  disabled={isPublishing}
                  className="w-full text-sm text-muted-foreground hover:text-foreground"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {/* Login Step */}
          {step === 'login' && (
            <div className="space-y-5">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-violet-200 to-sky-200 dark:from-violet-800 dark:to-sky-800 flex items-center justify-center">
                  <Key className="w-7 h-7 text-violet-700 dark:text-violet-300" />
                </div>
                <h2 className="text-xl font-semibold">Welcome back</h2>
              </div>

              {/* Extension login if available */}
              {hasExtension ? (
                <>
                  <Button
                    onClick={handleExtensionLogin}
                    disabled={isLoggingIn}
                    className="w-full py-5 rounded-full"
                  >
                    {isLoggingIn ? 'Logging in...' : 'Log in with extension'}
                  </Button>

                  {/* Remote signer option - button when extension available */}
                  <Button
                    variant="outline"
                    onClick={goToConnect}
                    className="w-full rounded-full"
                  >
                    Use remote signer
                  </Button>

                  {/* Key input - collapsible when extension available */}
                  <Collapsible open={showMoreOptions} onOpenChange={setShowMoreOptions}>
                    <CollapsibleTrigger className="w-full flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground py-2">
                      <span>Use secret key</span>
                      <ChevronDown className={`w-4 h-4 transition-transform ${showMoreOptions ? 'rotate-180' : ''}`} />
                    </CollapsibleTrigger>

                    <CollapsibleContent className="space-y-3">
                      <div className="space-y-2">
                        <Input
                          type="password"
                          value={loginNsec}
                          onChange={(e) => {
                            setLoginNsec(e.target.value);
                            setLoginError('');
                          }}
                          placeholder="nsec1..."
                          className="rounded-xl"
                        />
                        {loginError && (
                          <p className="text-sm text-red-500 text-center">{loginError}</p>
                        )}
                      </div>

                      <div className="flex gap-2">
                        <Button
                          onClick={handleLogin}
                          disabled={isLoggingIn || !loginNsec.trim()}
                          className="flex-1 rounded-full"
                        >
                          {isLoggingIn ? 'Logging in...' : 'Log in'}
                        </Button>
                        <input
                          type="file"
                          accept=".txt"
                          className="hidden"
                          ref={fileInputRef}
                          onChange={handleFileUpload}
                        />
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={() => fileInputRef.current?.click()}
                          className="rounded-full"
                        >
                          <Upload className="w-4 h-4" />
                        </Button>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>
                </>
              ) : (
                <>
                  {/* No extension - show key input directly */}
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Input
                        type="password"
                        value={loginNsec}
                        onChange={(e) => {
                          setLoginNsec(e.target.value);
                          setLoginError('');
                        }}
                        placeholder="nsec1..."
                        className="rounded-xl"
                      />
                      {loginError && (
                        <p className="text-sm text-red-500 text-center">{loginError}</p>
                      )}
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={handleLogin}
                        disabled={isLoggingIn || !loginNsec.trim()}
                        className="flex-1 rounded-full"
                      >
                        {isLoggingIn ? 'Logging in...' : 'Log in'}
                      </Button>
                      <input
                        type="file"
                        accept=".txt"
                        className="hidden"
                        ref={fileInputRef}
                        onChange={handleFileUpload}
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => fileInputRef.current?.click()}
                        className="rounded-full"
                      >
                        <Upload className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Remote signer option - link when no extension */}
                  <button
                    onClick={goToConnect}
                    className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Use remote signer
                  </button>
                </>
              )}

              <button
                onClick={() => setStep('welcome')}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
              >
                Back
              </button>
            </div>
          )}

          {/* Connect Step */}
          {step === 'connect' && (
            <div className="space-y-5">
              <div className="text-center space-y-2">
                <div className="w-14 h-14 mx-auto rounded-full bg-gradient-to-br from-sky-200 to-emerald-200 dark:from-sky-800 dark:to-emerald-800 flex items-center justify-center">
                  <ExternalLink className="w-7 h-7 text-sky-700 dark:text-sky-300" />
                </div>
                <h2 className="text-xl font-semibold">Connect signer</h2>
                <p className="text-muted-foreground text-sm">
                  {isMobile ? 'Open your signer app' : 'Scan with your signer app'}
                </p>
              </div>

              <div className='flex flex-col items-center space-y-4'>
                {connectError ? (
                  <div className='flex flex-col items-center space-y-3 py-4'>
                    <p className='text-sm text-red-500 text-center'>{connectError}</p>
                    <Button variant='outline' onClick={handleConnectRetry} className="rounded-full">
                      Try again
                    </Button>
                  </div>
                ) : nostrConnectUri ? (
                  <>
                    {/* QR Code - only show on desktop */}
                    {!isMobile && (
                      <div className='p-4 bg-white rounded-2xl shadow-sm'>
                        <QRCodeCanvas
                          value={nostrConnectUri}
                          size={160}
                          level='M'
                        />
                      </div>
                    )}

                    {/* Status message */}


                    {/* Open Signer App button - primary action on mobile */}
                    {isMobile && (
                      <Button
                        onClick={handleOpenSignerApp}
                        className="w-full py-5 rounded-full bg-gradient-to-r from-sky-400 to-emerald-400 hover:from-sky-500 hover:to-emerald-500 text-white"
                      >
                        <ExternalLink className='w-5 h-5 mr-2' />
                        Open Signer App
                      </Button>
                    )}

                    {/* Copy button */}
                    <Button
                      variant='outline'
                      className='rounded-full'
                      onClick={handleCopyUri}
                    >
                      {uriCopied ? (
                        <>
                          <Check className='w-4 h-4 mr-2' />
                          Copied!
                        </>
                      ) : (
                        <>
                          <Copy className='w-4 h-4 mr-2' />
                          Copy link
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <div className='flex items-center justify-center h-[100px]'>
                    <Loader2 className='w-8 h-8 animate-spin text-muted-foreground' />
                  </div>
                )}
              </div>

              {/* Manual bunker URI */}
              <Collapsible open={showBunkerInput} onOpenChange={setShowBunkerInput}>
                <CollapsibleTrigger className="w-full flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground py-2">
                  <span>Enter bunker URI</span>
                  {showBunkerInput ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </CollapsibleTrigger>

                <CollapsibleContent className="space-y-3 pt-2">
                  <Input
                    value={bunkerUri}
                    onChange={(e) => setBunkerUri(e.target.value)}
                    placeholder='bunker://...'
                    className='rounded-xl text-sm'
                  />
                  <Button
                    variant='outline'
                    onClick={handleBunkerLogin}
                    disabled={isLoggingIn || !bunkerUri.trim() || !bunkerUri.startsWith('bunker://')}
                    className='w-full rounded-full'
                  >
                    {isLoggingIn ? 'Connecting...' : 'Connect'}
                  </Button>
                </CollapsibleContent>
              </Collapsible>

              <button
                onClick={() => setStep('login')}
                className="w-full text-sm text-muted-foreground hover:text-foreground"
              >
                Back
              </button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AuthDialog;
